import { useState, useMemo } from "react";
import { mockSalesHistory, mockCurrentDealers } from "@/mocks/dealerData";

type TabType = "sales" | "current";
type PeriodType = "daily" | "weekly" | "monthly" | "yearly" | "custom";

interface SalesRecord {
  id: string;
  productName: string;
  brand: string;
  quantity: number;
  amount: number;
  cost: number;
  profit: number;
  paymentMethod: string;
  date: string;
  dealerName: string;
}

interface DealerRecord {
  id: string;
  name: string;
  phone: string;
  address: string;
  balance: number;
}

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState<TabType>("sales");
  const [period, setPeriod] = useState<PeriodType>("daily");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedSale, setSelectedSale] = useState<SalesRecord | null>(null);
  const [selectedDealer, setSelectedDealer] = useState<DealerRecord | null>(null);

  const filteredSales = useMemo(() => {
    let data = [...mockSalesHistory];
    if (period === "daily") {
      const today = "2026-07-14";
      data = data.filter((s) => s.date.startsWith(today));
    } else if (period === "weekly") {
      data = data.filter((s) => s.date >= "2026-07-08");
    } else if (period === "monthly") {
      data = data.filter((s) => s.date.startsWith("2026-07"));
    } else if (period === "yearly") {
      data = data.filter((s) => s.date.startsWith("2026"));
    } else if (period === "custom" && dateFrom && dateTo) {
      data = data.filter((s) => s.date >= dateFrom && s.date <= dateTo);
    }
    return data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [period, dateFrom, dateTo]);

  const totalAmount = filteredSales.reduce((sum, s) => sum + s.amount, 0);
  const totalProfit = filteredSales.reduce((sum, s) => sum + s.profit, 0);
  const totalCost = filteredSales.reduce((sum, s) => sum + s.cost, 0);

  const byPayment = useMemo(() => {
    const map: Record<string, number> = {};
    filteredSales.forEach((s) => { map[s.paymentMethod] = (map[s.paymentMethod] || 0) + s.amount; });
    return map;
  }, [filteredSales]);

  const byBrand = useMemo(() => {
    const map: Record<string, number> = {};
    filteredSales.forEach((s) => { map[s.brand] = (map[s.brand] || 0) + s.amount; });
    return map;
  }, [filteredSales]);

  const handlePrint = () => {
    const title = activeTab === "sales" ? "Satış Raporu" : "Cari Hesap Raporu";
    const originalTitle = document.title;
    document.title = `ART POS - ${title}`;
    window.print();
    document.title = originalTitle;
  };

  const handleExportCSV = () => {
    const lines: string[] = [];
    if (activeTab === "sales") {
      lines.push("Tarih,Saat,\u00dc;r\u00fc;n,Marka,Adet,Tutar,Maliyet,K\u00e2;r,\u00d6;deme,Cari Bayi");
      filteredSales.forEach((s) => {
        const d = new Date(s.date);
        lines.push(`${d.toLocaleDateString("tr-TR")},${d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })},"${s.productName}","${s.brand}",${s.quantity},${s.amount},${s.cost},${s.profit},${s.paymentMethod},${s.dealerName || "-"}`);
      });
      lines.push(`,,,,TOPLAM:,${totalAmount},${totalCost},${totalProfit},,`);
    } else {
      lines.push("Cari Bayi,Telefon,Adres,Bakiye");
      mockCurrentDealers.forEach((d) => {
        lines.push(`"${d.name}","${d.phone}","${d.address}",${d.balance}`);
      });
    }
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `artpos_${activeTab}_raporu_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const paymentLabels: Record<string, string> = { nakit: "Nakit", pos: "POS", iban: "IBAN", cari: "Cari" };
  const paymentColors: Record<string, string> = { nakit: "bg-primary-100 text-primary-700", pos: "bg-accent-100 text-accent-700", iban: "bg-secondary-100 text-secondary-700", cari: "bg-primary-100 text-primary-700" };

  return (
    <div className="max-w-7xl mx-auto print-content">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="reports-title">Raporlar</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">Sat\u0131\u015f ve cari hesap raporlar\u0131n\u0131 g\u00f6;r\u00fc;nt\u00fc;leyin</p>
        </div>
        <div className="flex items-center gap-2 no-print">
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-printer-line"></i> Yazdır
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3 py-2 bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-excel-2-line"></i> Excel (CSV)
          </button>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print-header text-center mb-6">
        <img
          src="https://public.readdy.ai/ai/img_res/0bc3c21d-f175-4039-a061-cbb43a619b2e.png"
          alt="ART POS"
          className="w-24 h-24 mx-auto object-contain mb-2"
        />
        <h2 className="text-xl font-bold">ART POS - Barkodlu Sat\u0131\u015f Sistemleri</h2>
        <p className="text-sm text-gray-600 mt-1">{activeTab === "sales" ? "Sat\u0131\u015f Raporu" : "Cari Hesap Raporu"}</p>
        <p className="text-xs text-gray-500 mt-0.5">{new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
        <hr className="my-4 border-gray-300" />
      </div>

      {/* Tab switcher */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-6 no-print">
        {[{ key: "sales", label: "Sat\u0131\u015f Raporu", icon: "ri-shopping-cart-2-line" }, { key: "current", label: "Cari Hesap Raporu", icon: "ri-user-star-line" }].map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key as TabType)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab.key ? "bg-background-50 text-foreground-950" : "text-foreground-500 hover:text-foreground-700"}`}>
            <i className={tab.icon}></i> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "sales" ? (
        <>
          {/* Period filter */}
          <div className="flex flex-wrap items-center gap-2 mb-4 no-print overflow-x-auto pb-1">
            {[{ key: "daily", label: "G\u00fc;nl\u00fc;k" }, { key: "weekly", label: "Haftal\u0131;k" }, { key: "monthly", label: "Ayl\u0131;k" }, { key: "yearly", label: "Y\u0131;ll\u0131;k" }, { key: "custom", label: "Tarih Aral\u0131;\u011f\u0131;" }].map((p) => (
              <button key={p.key} onClick={() => setPeriod(p.key as PeriodType)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${period === p.key ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500 hover:text-foreground-700"}`}>{p.label}</button>
            ))}
            {period === "custom" && (
              <div className="flex items-center gap-2 ml-2">
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-2 py-1.5 bg-background-100 border border-background-200/70 rounded-md text-xs text-foreground-950 focus:outline-none focus:ring-1 focus:ring-primary-400" />
                <span className="text-xs text-foreground-400">-</span>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-2 py-1.5 bg-background-100 border border-background-200/70 rounded-md text-xs text-foreground-950 focus:outline-none focus:ring-1 focus:ring-primary-400" />
              </div>
            )}
          </div>

          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <p className="text-xs text-foreground-400 mb-1">Toplam Sat\u0131\u015f</p>
              <p className="text-xl font-bold text-foreground-950">\u20ba;{totalAmount.toLocaleString("en-US")}</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <p className="text-xs text-foreground-400 mb-1">Toplam K\u00e2;r</p>
              <p className="text-xl font-bold text-primary-600">\u20ba;{totalProfit.toLocaleString("en-US")}</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <p className="text-xs text-foreground-400 mb-1">Toplam Maliyet</p>
              <p className="text-xl font-bold text-foreground-950">\u20ba;{totalCost.toLocaleString("en-US")}</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <p className="text-xs text-foreground-400 mb-1">\u0130;\u015f;lem Say\u0131;s\u0131;</p>
              <p className="text-xl font-bold text-foreground-950">{filteredSales.length}</p>
            </div>
          </div>

          {/* Payment breakdown */}
          <div className="flex flex-wrap gap-3 mb-6">
            {Object.entries(byPayment).map(([method, amount]) => (
              <div key={method} className={`px-3 py-1.5 rounded-full text-xs font-medium ${paymentColors[method] || "bg-background-100 text-foreground-600"}`}>
                {paymentLabels[method] || method}: \u20ba;{amount.toLocaleString("en-US")}
              </div>
            ))}
            {Object.entries(byBrand).map(([brand, amount]) => (
              <div key={brand} className="px-3 py-1.5 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700">
                {brand}: \u20ba;{amount.toLocaleString("en-US")}
              </div>
            ))}
          </div>

          {/* Sales table */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
            {/* Desktop Table */}
            <div className="overflow-x-auto hidden md:block">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="bg-background-100 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 no-print">#</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Tarih</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Ürün</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Marka</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">Adet</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Tutar</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right no-print">Kâr</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Ödeme</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 no-print">Cari</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.map((s, idx) => (
                    <tr
                      key={s.id}
                      onClick={() => setSelectedSale(s)}
                      className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 text-foreground-400 text-xs no-print">{idx + 1}</td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">{new Date(s.date).toLocaleDateString("tr-TR")} {new Date(s.date).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-4 py-3 font-medium text-foreground-900">{s.productName}</td>
                      <td className="px-4 py-3 text-foreground-600">{s.brand}</td>
                      <td className="px-4 py-3 text-foreground-600 text-center">{s.quantity}</td>
                      <td className="px-4 py-3 text-foreground-900 text-right font-medium">${s.amount.toLocaleString("en-US")}</td>
                      <td className="px-4 py-3 text-primary-600 text-right font-medium no-print">${s.profit.toLocaleString("en-US")}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${paymentColors[s.paymentMethod] || "bg-background-100 text-foreground-600"}`}>
                          {paymentLabels[s.paymentMethod] || s.paymentMethod}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 text-xs no-print">{s.dealerName || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-background-200/70">
              {filteredSales.length === 0 ? (
                <div className="text-center py-12 text-foreground-400">
                  <i className="ri-inbox-line text-3xl block mb-2"></i>
                  <p className="text-sm">Bu dönemde satış kaydı bulunmuyor.</p>
                </div>
              ) : (
                filteredSales.map((s) => (
                  <div key={s.id} onClick={() => setSelectedSale(s)} className="px-4 py-3 cursor-pointer active:bg-background-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate">{s.productName}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-foreground-500">{s.brand}</span>
                          <span className="text-xs text-foreground-400">{s.quantity}x</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${paymentColors[s.paymentMethod] || "bg-background-100 text-foreground-600"}`}>
                            {paymentLabels[s.paymentMethod] || s.paymentMethod}
                          </span>
                          <span className="text-xs text-foreground-400">
                            {new Date(s.date).toLocaleDateString("tr-TR")} {new Date(s.date).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-foreground-950">${s.amount.toLocaleString("en-US")}</p>
                        <p className="text-xs text-primary-600">Kâr: ${s.profit.toLocaleString("en-US")}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {filteredSales.length === 0 && (
              <div className="hidden md:block text-center py-12 text-foreground-400">
                <i className="ri-inbox-line text-3xl block mb-2"></i>
                <p className="text-sm">Bu dönemde satış kaydı bulunmuyor.</p>
              </div>
            )}
          </div>

          {/* Print summary */}
          <div className="hidden print-summary mt-8 pt-4 border-t border-gray-300">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-foreground-500 text-center">Toplam Satış</p>
                <p className="text-lg font-bold">${totalAmount.toLocaleString("en-US")}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 text-center">Toplam Kâr</p>
                <p className="text-lg font-bold">${totalProfit.toLocaleString("en-US")}</p>
              </div>
              <div>
                <p className="text-xs text-foreground-500 text-center">İşlem Sayısı</p>
                <p className="text-lg font-bold">{filteredSales.length}</p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Current account summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <p className="text-xs text-foreground-400 mb-1">Toplam Cari Bor\u00e7;</p>
              <p className="text-xl font-bold text-foreground-950">\u20ba;{mockCurrentDealers.reduce((sum, d) => sum + d.balance, 0).toLocaleString("en-US")}</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <p className="text-xs text-foreground-400 mb-1">Aktif Cari Say\u0131;s\u0131;</p>
              <p className="text-xl font-bold text-primary-600">{mockCurrentDealers.length}</p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-4">
              <p className="text-xs text-foreground-400 mb-1">Ortalama Bakiye</p>
              <p className="text-xl font-bold text-foreground-950">\u20ba;{(mockCurrentDealers.reduce((sum, d) => sum + d.balance, 0) / mockCurrentDealers.length).toLocaleString("en-US")}</p>
            </div>
          </div>

          {/* Current account table */}
          <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
            {/* Desktop Table */}
            <div className="overflow-x-auto hidden md:block">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="bg-background-100 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 no-print">#</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Cari Bayi</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Telefon</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500">Adres</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Bakiye</th>
                    <th className="px-4 py-3 text-xs font-medium text-foreground-500 no-print">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {mockCurrentDealers.map((d, idx) => (
                    <tr
                      key={d.id}
                      onClick={() => setSelectedDealer(d)}
                      className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 text-foreground-400 text-xs no-print">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium text-foreground-900">{d.name}</td>
                      <td className="px-4 py-3 text-foreground-600">{d.phone}</td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">{d.address}</td>
                      <td className="px-4 py-3 text-right font-medium text-primary-600">${d.balance.toLocaleString("en-US")}</td>
                      <td className="px-4 py-3 no-print"><span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">Aktif</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden divide-y divide-background-200/70">
              {mockCurrentDealers.map((d) => (
                <div key={d.id} onClick={() => setSelectedDealer(d)} className="px-4 py-3 cursor-pointer active:bg-background-100">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-900">{d.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {d.phone && <span className="text-xs text-foreground-500"><i className="ri-phone-line text-xs mr-1"></i>{d.phone}</span>}
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">Aktif</span>
                      </div>
                      {d.address && <p className="text-xs text-foreground-400 mt-1 truncate">{d.address}</p>}
                    </div>
                    <p className="text-sm font-bold text-primary-600 shrink-0">${d.balance.toLocaleString("en-US")}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden print-summary mt-8 pt-4 border-t border-gray-300">
            <p className="text-xs text-gray-500 text-center">Toplam Cari Bor\u00e7;: \u20ba;{mockCurrentDealers.reduce((sum, d) => sum + d.balance, 0).toLocaleString("en-US")}</p>
          </div>
        </>
      )}

      {/* Sale Detail Modal */}
      {selectedSale && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 no-print" onClick={() => setSelectedSale(null)}>
          <div className="bg-background-50 rounded-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-background-200/70 flex items-center justify-between">
              <h4 className="text-base font-semibold text-foreground-950">Sat\u0131\u015f Detayı;</h4>
              <button onClick={() => setSelectedSale(null)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-foreground-400 mb-0.5">Tarih / Saat</p>
                  <p className="text-sm font-medium text-foreground-900">{new Date(selectedSale.date).toLocaleDateString("tr-TR")} {new Date(selectedSale.date).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-400 mb-0.5">\u00d6;deme Y\u00f6;ntemi</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${paymentColors[selectedSale.paymentMethod] || "bg-background-100 text-foreground-600"}`}>
                    {paymentLabels[selectedSale.paymentMethod] || selectedSale.paymentMethod}
                  </span>
                </div>
              </div>
              <hr className="border-background-200/70" />
              <div>
                <p className="text-xs text-foreground-400 mb-0.5">\u00dc;r\u00fc;n</p>
                <p className="text-base font-semibold text-foreground-950">{selectedSale.productName}</p>
                <p className="text-sm text-foreground-500">Marka: {selectedSale.brand}</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-foreground-400">Adet</p>
                  <p className="text-lg font-bold text-foreground-900">{selectedSale.quantity}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-foreground-400">Birim Fiyat</p>
                  <p className="text-lg font-bold text-foreground-900">\u20ba;{(selectedSale.amount / selectedSale.quantity).toLocaleString("en-US")}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3 text-center">
                  <p className="text-xs text-foreground-400">Birim Maliyet</p>
                  <p className="text-lg font-bold text-foreground-900">\u20ba;{(selectedSale.cost / selectedSale.quantity).toLocaleString("en-US")}</p>
                </div>
              </div>
              <hr className="border-background-200/70" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-foreground-400">Toplam Tutar</p>
                  <p className="text-lg font-bold text-foreground-950">\u20ba;{selectedSale.amount.toLocaleString("en-US")}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">Toplam Maliyet</p>
                  <p className="text-lg font-bold text-foreground-700">\u20ba;{selectedSale.cost.toLocaleString("en-US")}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">K\u00e2;r</p>
                  <p className="text-lg font-bold text-primary-600">\u20ba;{selectedSale.profit.toLocaleString("en-US")}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-400">K\u00e2;r Oran\u0131;</p>
                  <p className="text-lg font-bold text-primary-600">%{((selectedSale.profit / selectedSale.amount) * 100).toFixed(1)}</p>
                </div>
              </div>
              {selectedSale.dealerName && selectedSale.dealerName !== "-" && (
                <>
                  <hr className="border-background-200/70" />
                  <div className="flex items-center gap-2">
                    <i className="ri-building-line text-foreground-400"></i>
                    <span className="text-sm text-foreground-600">Cari Bayi: <strong className="text-foreground-900">{selectedSale.dealerName}</strong></span>
                  </div>
                </>
              )}
            </div>
            <div className="px-6 py-4 border-t border-background-200/70 flex gap-2">
              <button
                onClick={() => {
                  setSelectedSale(null);
                  handlePrint();
                }}
                className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
              >
                <i className="ri-printer-line"></i> Yazd\u0131;r
              </button>
              <button
                onClick={() => setSelectedSale(null)}
                className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dealer Detail Modal */}
      {selectedDealer && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 no-print" onClick={() => setSelectedDealer(null)}>
          <div className="bg-background-50 rounded-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-background-200/70 flex items-center justify-between">
              <h4 className="text-base font-semibold text-foreground-950">Cari Hesap Detayı;</h4>
              <button onClick={() => setSelectedDealer(null)} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-building-line text-primary-600 text-xl"></i>
                </div>
                <div>
                  <p className="text-base font-semibold text-foreground-950">{selectedDealer.name}</p>
                  <p className="text-xs text-foreground-500">Cari Bayi</p>
                </div>
              </div>
              <hr className="border-background-200/70" />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-foreground-400 mb-0.5">Telefon</p>
                  <p className="text-sm font-medium text-foreground-900">{selectedDealer.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-400 mb-0.5">Adres</p>
                  <p className="text-sm font-medium text-foreground-900">{selectedDealer.address}</p>
                </div>
              </div>
              <div className="bg-background-100 rounded-xl p-4">
                <p className="text-xs text-foreground-400 mb-1">Mevcut Bakiye</p>
                <p className="text-2xl font-bold text-primary-600">\u20ba;{selectedDealer.balance.toLocaleString("en-US")}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-400">Son Tahsilat</p>
                  <p className="text-sm font-medium text-foreground-900">\u20ba;5.000,00</p>
                  <p className="text-xs text-foreground-400">10.07.2026</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-400">Son Sat\u0131\u015f</p>
                  <p className="text-sm font-medium text-foreground-900">\u20ba;42.999,00</p>
                  <p className="text-xs text-foreground-400">14.07.2026</p>
                </div>
              </div>
              <div className="bg-background-100 rounded-lg p-3">
                <p className="text-xs text-foreground-400 mb-1">Notlar</p>
                <p className="text-sm text-foreground-600">D\u00fc;zenli \u00f6;deme yapan, g\u00fc;venilir bir cari hesapt\u0131;r.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-background-200/70 flex gap-2">
              <button
                onClick={() => {
                  setSelectedDealer(null);
                  handlePrint();
                }}
                className="flex-1 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
              >
                <i className="ri-printer-line"></i> Yazd\u0131;r
              </button>
              <button
                onClick={() => setSelectedDealer(null)}
                className="flex-1 py-2.5 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}